import { useState, useRef, useCallback, useLayoutEffect } from 'react'
import { gsap } from 'gsap'
import {
  faChevronRight,
  faChevronUp,
  faReply,
} from '@fortawesome/pro-solid-svg-icons'
import { Icon, Button, Badge, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import capitalize from 'lodash/capitalize'
import type { TicketSummary, TicketComment } from '../lib/types'
import { postTicketCommentPrompt } from '../lib/messageTabs'
import { formatShortDateTimeWithZone } from '@/utils/dateHelpers'

const statusBadgeColor: Record<
  string,
  'green' | 'yellow' | 'blue' | 'orange' | 'gray'
> = {
  open: 'green',
  pending: 'yellow',
  new: 'blue',
  hold: 'orange',
  solved: 'gray',
  closed: 'gray',
}

const dotColor: Record<string, string> = {
  requester: 'bg-green-100',
  agent: 'bg-neutral-400',
}

const authorLabel = (type: TicketComment['authorType']) =>
  type === 'requester' ? 'You' : 'Heard Support'

interface TicketCommentEntryProps {
  comment: TicketComment
  isLast: boolean
}

const TicketCommentEntry = ({ comment, isLast }: TicketCommentEntryProps) => (
  <div
    className={mtc(
      'group/entry relative -ml-1 pl-6 py-1.5 rounded-lg transition-colors duration-150',
      isLast ? 'bg-green-0/60' : 'hover:bg-neutral-25'
    )}
  >
    {/* Dot */}
    <div
      className={mtc(
        'absolute left-1 top-3 w-[7px] h-[7px] rounded-full ring-2',
        isLast
          ? 'ring-green-0/60'
          : 'ring-neutral-0 group-hover/entry:ring-neutral-25',
        dotColor[comment.authorType] ?? 'bg-neutral-300'
      )}
    />

    {/* Content */}
    <div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <Text
          variant={TypographyStyles.LABEL_XS}
          className="font-semibold text-neutral-800"
        >
          {authorLabel(comment.authorType)}
        </Text>
        <span className="text-[11px] text-neutral-400">
          {formatShortDateTimeWithZone(comment.createdAt)}
        </span>
        {isLast && (
          <span className="text-[10px] font-semibold text-green-100 uppercase tracking-wider">
            Latest
          </span>
        )}
      </div>
      <Text variant={TypographyStyles.BODY_SM} className="mt-0.5">
        {comment.body}
      </Text>
    </div>
  </div>
)

interface TicketHistoryProps {
  comments: TicketComment[]
}

const TicketHistory = ({ comments }: TicketHistoryProps) => {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <div>
      <button
        type="button"
        onClick={() => setCollapsed((c) => !c)}
        className="flex items-center justify-between w-full mb-3 bg-transparent border-none text-left cursor-pointer p-0"
      >
        <Text
          variant={TypographyStyles.LABEL_SM}
          color={TextColors.EMPHASIS}
          className="font-semibold"
        >
          Message history
        </Text>
        <Icon
          icon={faChevronUp}
          className={mtc(
            'text-[10px] text-neutral-400 transition-transform duration-200',
            collapsed && 'rotate-180'
          )}
        />
      </button>

      {!collapsed && (
        <div className="relative ml-1.5">
          {/* Vertical line */}
          <div className="absolute left-[3px] top-2 bottom-2 w-px bg-neutral-200" />

          <div className="flex flex-col gap-4">
            {comments.map((comment, i) => (
              <TicketCommentEntry
                key={`${comment.createdAt}-${comment.authorType}`}
                comment={comment}
                isLast={i === comments.length - 1}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Individual ticket item with GSAP expand/collapse ──────────────────────────

interface TicketItemProps {
  ticket: TicketSummary
  onSend?: (message: string) => void
}

export const TicketItem = ({ ticket, onSend }: TicketItemProps) => {
  const [expanded, setExpanded] = useState(false)
  const [replyingTo, setReplyingTo] = useState(false)
  const [replyText, setReplyText] = useState('')

  const collapsedRef = useRef<HTMLButtonElement>(null)
  const expandedRef = useRef<HTMLDivElement>(null)
  // Tracks whether an animation is in-flight to prevent double-triggers
  const animatingRef = useRef(false)

  // Set initial GSAP state: expanded panel hidden, collapsed row visible
  useLayoutEffect(() => {
    if (expandedRef.current) {
      gsap.set(expandedRef.current, { height: 0, autoAlpha: 0 })
    }
  }, [])

  const open = useCallback(() => {
    if (animatingRef.current) return
    animatingRef.current = true

    setExpanded(true)

    gsap.to(collapsedRef.current, {
      autoAlpha: 0,
      height: 0,
      duration: 0.18,
      ease: 'power2.in',
    })
    gsap.fromTo(
      expandedRef.current,
      { height: 0, autoAlpha: 0 },
      {
        height: 'auto',
        autoAlpha: 1,
        duration: 0.32,
        ease: 'power2.out',
        delay: 0.1,
        clearProps: 'height',
        onComplete: () => {
          animatingRef.current = false
        },
      }
    )
  }, [])

  const close = useCallback(() => {
    if (animatingRef.current) return
    animatingRef.current = true

    gsap.to(expandedRef.current, {
      height: 0,
      autoAlpha: 0,
      duration: 0.22,
      ease: 'power2.in',
      onComplete: () => {
        setExpanded(false)
        setReplyingTo(false)
        setReplyText('')
        animatingRef.current = false
        // Restore collapsed row inline styles that GSAP zeroed out
        if (collapsedRef.current) {
          gsap.set(collapsedRef.current, { clearProps: 'all' })
        }
      },
    })
  }, [])

  return (
    <div
      className={mtc(
        'rounded-xl overflow-hidden bg-neutral-0',
        expanded ? 'border-2 border-neutral-200' : 'border border-neutral-200'
      )}
    >
      {/* Collapsed row — always in DOM; GSAP hides it when expanded */}
      <button
        ref={collapsedRef}
        type="button"
        onClick={open}
        className={mtc(
          'flex items-center w-full gap-2.5 px-4 py-3',
          'bg-transparent border-none text-left cursor-pointer'
        )}
      >
        <Icon icon={faChevronRight} className="text-[10px] text-neutral-400" />

        <span className="flex-1 text-sm font-medium truncate text-neutral-800">
          <span className="text-neutral-500">#{ticket.ticketId}</span>
          {' · '}
          {ticket.subject}
        </span>

        <Badge
          variant="secondary"
          color={statusBadgeColor[ticket.status] ?? 'green'}
          size="sm"
          className="shrink-0"
        >
          {capitalize(ticket.status)}
        </Badge>
      </button>

      {/* Expanded panel — always in DOM; GSAP animates height 0 ↔ auto */}
      <div ref={expandedRef} style={{ overflow: 'hidden' }}>
        <div className="px-5 pt-4 pb-4">
          {/* Ticket header — click to collapse */}
          <button
            type="button"
            onClick={close}
            className="flex items-start gap-2 mb-3 w-full bg-transparent border-none text-left cursor-pointer p-0"
          >
            <div className="flex-1">
              <Text
                variant={TypographyStyles.BODY_XS}
                color={TextColors.DISABLED}
                className="mb-0.5"
              >
                Ticket #{ticket.ticketId}
              </Text>
              <Text
                variant={TypographyStyles.LABEL_MD}
                className="font-semibold text-neutral-800 leading-snug"
              >
                {ticket.subject}
              </Text>
            </div>
            <Icon
              icon={faChevronUp}
              className="text-[10px] text-neutral-400 mt-1.5"
            />
          </button>

          {/* Metadata row */}
          <div className="flex gap-8 mb-3 pb-3 border-b border-neutral-100">
            <div>
              <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">
                Status
              </div>
              <Badge
                variant="secondary"
                color={statusBadgeColor[ticket.status] ?? 'green'}
                size="sm"
              >
                {capitalize(ticket.status)}
              </Badge>
            </div>
            <div>
              <div className="text-[10px] font-semibold text-neutral-400 uppercase tracking-wider mb-1">
                Updated
              </div>
              <Text
                variant={TypographyStyles.BODY_SM}
                color={TextColors.EMPHASIS}
              >
                {formatShortDateTimeWithZone(ticket.updatedAt)}
              </Text>
            </div>
          </div>

          {/* Ticket history */}
          {ticket.comments.length > 0 && (
            <TicketHistory comments={ticket.comments} />
          )}

          {/* Add comment action */}
          {onSend && (
            <div className="mt-3 pt-3 border-t border-neutral-100">
              {replyingTo ? (
                <div className="flex flex-col gap-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a comment..."
                    rows={3}
                    className={mtc(
                      'w-full rounded-lg border border-neutral-200 px-3 py-2',
                      'text-sm text-neutral-800 placeholder:text-neutral-400',
                      'resize-none focus:outline-none focus:border-green-50'
                    )}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setReplyingTo(false)
                        setReplyText('')
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={!replyText.trim()}
                      onClick={() => {
                        if (!replyText.trim()) return
                        onSend(
                          postTicketCommentPrompt(
                            ticket.ticketId,
                            replyText.trim()
                          )
                        )
                        setReplyingTo(false)
                        setReplyText('')
                      }}
                    >
                      Send
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setReplyingTo(true)}
                  className={mtc(
                    'flex items-center gap-1.5 text-xs font-medium text-green-100',
                    'bg-transparent border-none cursor-pointer p-0',
                    'hover:text-green-200 transition-colors'
                  )}
                >
                  <Icon icon={faReply} className="text-[10px]" />
                  Add a comment
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
